import { format, isValid, parse } from 'date-fns';

/**
 * Formats a date in a human-readable format (e.g., Monday, January 1, 2023)
 */
export const formatDate = (date: Date | string | null): string => {
  if (!date) return 'No date provided';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return 'Invalid date';
    
    return format(dateObj, 'EEEE, MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error formatting date';
  }
};

/**
 * Formats a time in 12-hour format (e.g., 2:30 PM)
 */
export const formatTime = (date: Date | string | null): string => {
  if (!date) return 'No time provided';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return 'Invalid time';
    
    return format(dateObj, 'h:mm a');
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Error formatting time';
  }
};

/**
 * Formats a date in a compact format (e.g., Jan 1, 2023)
 */
export const formatCompactDate = (date: Date | string | null): string => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return 'Invalid date';
    
    return format(dateObj, 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Formats a date range in a readable format (e.g., Jan 1 - Jan 5, 2023)
 */
export const formatDateRange = (start: Date | string | null, end: Date | string | null): string => {
  if (!start || !end) return 'Date range not available';
  
  try {
    const startDate = typeof start === 'string' ? new Date(start) : start;
    const endDate = typeof end === 'string' ? new Date(end) : end;
    
    if (!isValid(startDate) || !isValid(endDate)) return 'Invalid date range';
    
    // If same year
    if (startDate.getFullYear() === endDate.getFullYear()) {
      // If same month
      if (startDate.getMonth() === endDate.getMonth()) {
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`;
      }
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Error formatting date range';
  }
};

/**
 * Parses a date string in the format YYYY-MM-DD
 */
export const parseDate = (dateString: string): Date | null => {
  try {
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Returns a relative time string (e.g., "Today", "Yesterday", "In 3 days")
 */
export const getRelativeTimeString = (date: Date | string): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const targetDate = new Date(dateObj);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    return `${Math.abs(diffDays)} days ago`;
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return 'Unknown';
  }
}; 